import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import LZString from 'lz-string';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './BuyerInvoice.css';
import BuyerInvoicePrintContent from './components/BuyerInvoicePrintContent';

export const BuyerInvoice = () => {
    const [searchParams] = useSearchParams();
    const [invoiceData, setInvoiceData] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [isFailed, setIsFailed] = useState(false);
    const printRef = useRef(null);
    const hasExecuted = useRef(false);

    // بستن پنجره در مرورگر عادی و مینی‌اپ تلگرام
    const handleCloseWindow = () => {
        if (window.Telegram?.WebApp?.close) {
            window.Telegram.WebApp.close();
        }
        window.close();
    };

    useEffect(() => {
        try {
            let rawData = searchParams.get('data') || searchParams.get('startapp');
            if (!rawData && window.location.hash) {
                const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
                rawData = hashParams.get('data');
            }

            if (!rawData) {
                setErrorMessage('اطلاعات فاکتور ارسال نشده است.');
                return;
            }

            const decompressed = LZString.decompressFromEncodedURIComponent(rawData);
            if (!decompressed) {
                setErrorMessage('داده‌های ارسالی نامعتبر است.');
                return;
            }

            setInvoiceData(JSON.parse(decompressed));
        } catch (err) {
            setErrorMessage('خطا در پردازش اطلاعات فاکتور.');
        }
    }, [searchParams]);

    const handleDownloadPdf = useCallback(async () => {
        if (!invoiceData) return;

        const targetElement = document.querySelector('.receipt-paper') || printRef.current;
        if (!targetElement) {
            setIsFailed(true);
            return;
        }

        try {
            const canvas = await html2canvas(targetElement, { 
                scale: 3, 
                useCORS: true, 
                backgroundColor: '#ffffff',
                logging: false,
                windowWidth: targetElement.scrollWidth || 360,
                windowHeight: targetElement.scrollHeight || 600,
                onclone: (documentClone, elementClone) => {
                    const walker = documentClone.createTreeWalker(
                        elementClone,
                        NodeFilter.SHOW_TEXT,
                        null,
                        false
                    );

                    let node;
                    while ((node = walker.nextNode())) {
                        if (node.nodeValue && (node.nodeValue.includes('(') || node.nodeValue.includes(')'))) {
                            node.nodeValue = node.nodeValue
                                .replace(/\(/g, '___OPEN___')
                                .replace(/\)/g, '(')
                                .replace(/___OPEN___/g, ')');
                        }
                    }
                }
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const THERMAL_WIDTH_MM = 80;
            const MARGIN_MM = 2; 
            const PRINT_WIDTH_MM = THERMAL_WIDTH_MM - (MARGIN_MM * 2);
            const printHeightMM = (canvas.height * PRINT_WIDTH_MM) / canvas.width;

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [THERMAL_WIDTH_MM, printHeightMM + (MARGIN_MM * 2)],
                compress: true
            });

            pdf.addImage(imgData, 'JPEG', MARGIN_MM, MARGIN_MM, PRINT_WIDTH_MM, printHeightMM, undefined, 'FAST');
            
            const fileName = `Invoice-${invoiceData.factorNumber || invoiceData.id || Date.now()}.pdf`;

            const pdfBlob = pdf.output('blob');
            const blobUrl = URL.createObjectURL(pdfBlob);
            setPdfBlobUrl(blobUrl);

            // ایجاد لینک و تریگر دانلود خودکار
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // ۳.۵ ثانیه مکث برای اینکه فرصت تایید و دانلود در گوشی ایجاد شود و سپس بسته شود
            setTimeout(() => {
                handleCloseWindow();
            }, 3500);

        } catch (e) {
            console.error('Buyer PDF Generation Error:', e);
            setIsFailed(true);
        }
    }, [invoiceData]);

    useEffect(() => {
        if (invoiceData && !hasExecuted.current) {
            hasExecuted.current = true;
            const timer = setTimeout(() => {
                handleDownloadPdf();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [invoiceData, handleDownloadPdf]);

    // فقط اگر پارامترهای آدرس نامعتبر بود
    if (errorMessage) {
        return (
            <div className="buyer-print-state">
                <div className="state-box error">
                    <h3>خطا</h3>
                    <p>{errorMessage}</p>
                    <button 
                        type="button"
                        className="btn-close-window" 
                        style={{ marginTop: '14px', backgroundColor: '#64748b' }}
                        onClick={handleCloseWindow}
                    >
                        بستن صفحه
                    </button>
                </div>
            </div>
        );
    }

    // دکمه‌ها فقط و فقط در صورت خرابی رندر یا شکست دانلود نمایش داده می‌شوند
    if (isFailed) {
        return (
            <div className="buyer-print-state">
                <div className="state-box error">
                    <h3>خطا در دریافت فاکتور</h3>
                    <p style={{ color: '#475569', fontSize: '13px', margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        در دریافت خودکار فاکتور مشکلی رخ داد.<br />می‌توانید به صورت دستی اقدام کنید:
                    </p>

                    {pdfBlobUrl && (
                        <a 
                            href={pdfBlobUrl} 
                            download={`Invoice-${invoiceData?.factorNumber || invoiceData?.id || Date.now()}.pdf`}
                            className="btn-close-window"
                            style={{ display: 'block', textDecoration: 'none', textAlign: 'center', marginBottom: '10px' }}
                        >
                            دریافت دستی فایل PDF
                        </a>
                    )}

                    <button 
                        type="button"
                        className="btn-close-window" 
                        style={{ backgroundColor: '#64748b' }}
                        onClick={handleCloseWindow}
                    >
                        بستن پنجره
                    </button>
                </div>
            </div>
        );
    }

    // حالت عادی: لودینگ فول‌اسکرین روی صفحه می‌ماند تا زمانی که بعد از ۳.۵ ثانیه بسته شود
    return (
        <div className="buyer-invoice-page-container">
            <div className="fullscreen-loading-overlay">
                <div className="spinner"></div>
                <span style={{ marginTop: '16px', fontWeight: 'bold', fontSize: '15px' }}>در حال آماده‌سازی و دریافت فاکتور خرید...</span>
                <span style={{ fontSize: '12px', opacity: 0.75, marginTop: '8px' }}>لطفاً شکیبا باشید</span>
            </div>

            {/* محتوا در بک‌گراند رندر می‌شود تا html2canvas بتواند عکس بگیرد */}
            <div className="hidden-render-wrapper">
                <BuyerInvoicePrintContent invoiceData={invoiceData} contentRef={printRef} />
            </div>
        </div>
    );
};

export default BuyerInvoice;
